import { useEffect, useId, useRef } from 'react'
import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { CloseIcon } from '@/components/icons'
import { overlayVariants, sheetReducedVariants, sheetVariants, usePrefersReducedMotion } from '@/design-system/motion'
import { IconButton } from './IconButton'
import { useOverlayBehaviour } from './internal/use-overlay'
import './BottomSheet.css'

export type BottomSheetProps = {
  open: boolean
  onClose: () => void
  title?: string
  /** Sticky footer inside the sheet (spec §10.1). */
  footer?: ReactNode
  children: ReactNode
}

/**
 * iOS-style bottom sheet. Always offers a visible close action; Escape and
 * overlay tap also close. Reduced motion swaps the slide for a short fade.
 */
export function BottomSheet({ open, onClose, title, footer, children }: BottomSheetProps) {
  const titleId = useId()
  const sheetRef = useRef<HTMLDivElement>(null)
  const reducedMotion = usePrefersReducedMotion()
  useOverlayBehaviour(open, onClose)

  useEffect(() => {
    if (open) sheetRef.current?.focus()
  }, [open])

  return createPortal(
    <AnimatePresence>
      {open && (
        <div className="ui-sheet-root">
          <motion.div
            className="ui-sheet-overlay"
            variants={overlayVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            className="ui-sheet"
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            aria-label={title ? undefined : 'Scherm onderaan'}
            tabIndex={-1}
            ref={sheetRef}
            variants={reducedMotion ? sheetReducedVariants : sheetVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <span className="ui-sheet__handle" aria-hidden="true" />
            <div className="ui-sheet__header">
              {title ? (
                <h2 className="ui-sheet__title" id={titleId}>
                  {title}
                </h2>
              ) : (
                <span />
              )}
              <IconButton aria-label="Sluiten" onClick={onClose}>
                <CloseIcon size={20} />
              </IconButton>
            </div>
            <div className="ui-sheet__body">{children}</div>
            {footer && <div className="ui-sheet__footer">{footer}</div>}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
